/**
 * Számlák oldal — v2 helyesbítő, sztornó linkek, devizás támogatás
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { get, post } from '../../lib/api';
import { downloadFile } from '../../lib/api';
import { PageHeader, Button, Badge, Card, Input, Modal, EmptyState, LoadingOverlay, NumInput } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';
import PartnerAutocomplete from '../../components/PartnerAutocomplete';
import ActionsDropdown from '../../components/ActionsDropdown';
import MoneyInput from '../../components/ui/MoneyInput';
import LogoUploadModal from '../../components/LogoUploadModal';
import PostIssueModal from '../../components/PostIssueModal';
import { useFKeyBar } from '../../components/layout/FKeyBar';
import ModulumBadge from '../../components/ui/ModulumBadge';

interface Invoice {
  id: string; invoice_number: string; type: string; status: string;
  partner_snapshot_json?: { name: string }; partner_name?: string;
  partner_id?: string; partner_network_tenant_id?: string | null;
  issue_date: string; performance_date: string; due_date: string;
  subtotal_net: string; subtotal_vat: string; subtotal_gross: string;
  nav_status: string; notes: string;
  is_advance?: boolean;
  is_cancelled?: boolean;
  cancellation_invoice_id?: string;
  cancellation_invoice_number?: string;
  cancellation_invoice_type?: string;
  referenced_invoice_id?: string;
  referenced_invoice_number?: string;
  advance_source_invoice_ids?: string[];
  payment_method?: string;
  currency?: string;
  paid_amount?: string;
}

interface AdvanceLine { vatRate: string; lineNet: number; lineVat: number; lineGross: number; itemName: string; }
interface AdvanceInvoice {
  id: string; invoice_number: string; status: string;
  subtotal_net: number; subtotal_vat: number; subtotal_gross: number;
  lines?: AdvanceLine[];
  issue_date: string;
  usedInInvoice?: string | null;
}

const TYPE_MAP: Record<string, { label: string; variant: 'default' | 'info' | 'warning' | 'error' }> = {
  normal: { label: 'Normál', variant: 'default' },
  proforma: { label: 'Díjbekérő', variant: 'info' },
  corrective: { label: 'Helyesbítő', variant: 'warning' },
  cancellation: { label: 'Sztornó', variant: 'error' },
};

const STATUS_MAP: Record<string, { label: string; variant: 'default' | 'info' | 'success' | 'warning' | 'error' }> = {
  draft: { label: 'Vázlat', variant: 'default' },
  issued: { label: 'Kiállítva', variant: 'info' },
  paid: { label: 'Fizetve', variant: 'success' },
  partial: { label: 'Részben fizetve', variant: 'warning' },
  overdue: { label: 'Lejárt', variant: 'error' },
  cancelled: { label: 'Sztornózva', variant: 'error' },
};

const STATUS_TABS = ['Mind', 'Vázlat', 'Kiállítva', 'Fizetve', 'Részben fizetve', 'Lejárt', 'Sztornózva'];
const TYPE_TABS = ['Mind', 'Normál', 'Díjbekérő', 'Sztornó', 'Helyesbítő'];
const TYPE_API_MAP: Record<string, string> = { 'Normál': 'normal', 'Díjbekérő': 'proforma', 'Sztornó': 'cancellation', 'Helyesbítő': 'corrective' };
const PAYMENT_METHOD_LABELS: Record<string, string> = { transfer: 'Átutalás', cash: 'Készpénz', card: 'Bankkártya' };
const NAV_ICONS: Record<string, string> = { off: '−', pending: '⏳', accepted: '✅', rejected: '❌' };

function formatPrice(f: string | number) { return Math.round(Number(f)).toLocaleString('hu-HU') + ' Ft'; }
function formatDate(iso: string | undefined) { return iso ? new Date(iso).toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'; }

export default function InvoicesPage() {
  const { token } = useAuth();
  const { toast } = useToast();
  const [invoices, setInvoices] = useState<Invoice[]>([]);

  useFKeyBar([
    { key: 'F5', label: 'Új számla', action: () => setShowEditor(true) },
    { key: 'F6', label: 'PDF letöltés', action: () => { if (showDetail) downloadFile(`/invoices/${showDetail.id}/pdf`); } },
    { key: 'F8', label: 'Nyomtatás', action: () => { if (showDetail) window.print(); } },
  ]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('Mind');
  const [showEditor, setShowEditor] = useState(false);
  const [editorOrderId, setEditorOrderId] = useState<string | null>(null);
  const [showDetail, setShowDetail] = useState<Invoice | null>(null);
  const [showPayment, setShowPayment] = useState<Invoice | null>(null);
  const [showLogoUpload, setShowLogoUpload] = useState(false);
  const [showCorrective, setShowCorrective] = useState<Invoice | null>(null);
  const [postIssue, setPostIssue] = useState<{ id: string; number: string; type: string; partnerId: string; partnerName: string; isCash?: boolean } | null>(null);
  const [globalLoading, setGlobalLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState('Mind');
  const [partnerFilter, setPartnerFilter] = useState<{ id: string; name: string } | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [serverTotalPages, setServerTotalPages] = useState(1);
  const [editorPreselectedPartner, setEditorPreselectedPartner] = useState<{ id: string; name: string } | null>(null);

  // Date filter state — default: current month
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0,10));

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Highlight
  // Auto-open editor / detail from URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromOrder = params.get('fromOrder');
    const fromPartner = params.get('fromPartner');
    const fromPartnerName = params.get('partnerName');
    const highlight = params.get('highlight');
    const open = params.get('open');
    window.history.replaceState({}, '', window.location.pathname);
    if (fromOrder) { setEditorOrderId(fromOrder); setShowEditor(true); }
    if (fromPartner && fromPartnerName) {
      setEditorPreselectedPartner({ id: fromPartner, name: decodeURIComponent(fromPartnerName) });
      setShowEditor(true);
    }
    if (highlight || open) {
      const id = highlight || open;
      get<any>(`/invoices/${id}`).then(inv => { if (inv) setShowDetail(inv); }).catch(() => {});
    }
  }, []);

  // Debounce keresés
  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(search), 300); return () => clearTimeout(t); }, [search]);

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (debouncedSearch) p.set('search', debouncedSearch);
      if (activeTab !== 'Mind') {
        const sk = Object.entries(STATUS_MAP).find(([_, v]) => v.label === activeTab)?.[0];
        if (sk) p.set('status', sk);
      }
      if (typeFilter !== 'Mind') { const t = TYPE_API_MAP[typeFilter]; if (t) p.set('type', t); }
      if (dateFrom) p.set('date_from', dateFrom);
      if (dateTo) p.set('date_to', dateTo);
      if (partnerFilter) p.set('partner_id', partnerFilter.id);
      p.set('page', String(page));
      p.set('pageSize', String(pageSize));
      const data = await get<any>(`/invoices?${p}`);
      const list = Array.isArray(data) ? data : (data?.data || []);
      setInvoices(list);
      setTotalCount(data?.total ?? list.length);
      setServerTotalPages(Math.max(1, Math.ceil((data?.total ?? list.length) / (data?.pageSize ?? pageSize))));
    } catch { /* */ }
    setLoading(false);
  }, [debouncedSearch, activeTab, typeFilter, dateFrom, dateTo, partnerFilter, page, pageSize]);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  // Reset oldal ha szűrő változik
  useEffect(() => { setPage(1); }, [debouncedSearch, activeTab, typeFilter, dateFrom, dateTo, partnerFilter]);

  const totalPages = serverTotalPages;
  const paginated = invoices;

  function getTypeLabel(inv: Invoice) {
    if (inv.is_advance) return 'Előleg';
    if (inv.advance_source_invoice_ids?.length) return 'Végszámla';
    return TYPE_MAP[inv.type]?.label || 'Normál';
  }
  function getTypeVariant(inv: Invoice): 'default' | 'info' | 'warning' | 'error' {
    if (inv.is_advance) return 'warning';
    if (inv.advance_source_invoice_ids?.length) return 'info';
    return TYPE_MAP[inv.type]?.variant || 'default';
  }

  return (
    <div>
      <LoadingOverlay visible={globalLoading} text="PDF előkészítése..." />
      <PageHeader title="Számlák" description={`${totalCount} számla`} actions={<div style={{ display: 'flex', gap: '8px' }}><Button variant="ghost" size="sm" onClick={() => setShowLogoUpload(true)}>Logo</Button><Button onClick={() => setShowEditor(true)}>+ Új számla</Button></div>} />

      <div className="chips" style={{ marginBottom: '8px' }}>
        {STATUS_TABS.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} className={`chip${activeTab === tab ? ' active' : ''}`}>{tab}</button>
        ))}
      </div>
      <div className="chips" style={{ marginBottom: '14px' }}>
        {TYPE_TABS.map(tab => (
          <button key={tab} onClick={() => setTypeFilter(tab)} className={`chip${typeFilter === tab ? ' active' : ''}`}>{tab}</button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
        <Input placeholder="Keresés sorszám..." value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1, minWidth: '160px' }} />
        <div style={{ minWidth: '180px', flex: 1 }}>
          <PartnerAutocomplete label="" value={partnerFilter} onChange={setPartnerFilter} placeholder="Partner szűrő..." />
        </div>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="form-input" style={{ width: '140px', fontSize: '13px' }} title="Dátumtól" />
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="form-input" style={{ width: '140px', fontSize: '13px' }} title="Dátumig" />
        <div style={{ display: 'flex', gap: '4px' }}>
          <button onClick={() => { const d = new Date(); setDateFrom(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`); setDateTo(d.toISOString().slice(0,10)); }} className="btn btn-ghost" style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid var(--line)', borderRadius: 'var(--r)', cursor: 'pointer' }}>Ez a hónap</button>
          <button onClick={() => { const d = new Date(); d.setMonth(d.getMonth()-1); setDateFrom(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`); const last = new Date(d.getFullYear(), d.getMonth()+1, 0); setDateTo(last.toISOString().slice(0,10)); }} className="btn btn-ghost" style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid var(--line)', borderRadius: 'var(--r)', cursor: 'pointer' }}>Előző hónap</button>
          <button onClick={() => { setDateFrom(''); setDateTo(''); }} className="btn btn-ghost" style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid var(--line)', borderRadius: 'var(--r)', cursor: 'pointer' }}>Mind</button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--slate)' }}>Betöltés...</div>
      ) : paginated.length === 0 ? (
        <EmptyState icon="📄" title="Nincs számla" description="Hozz létre egyet a + Új számla gombbal." action={<Button size="sm" onClick={() => setShowEditor(true)}>+ Új számla</Button>} />
      ) : (
        <div style={{ display: 'grid', gap: '10px' }}>
          {paginated.map(inv => {
            const st = STATUS_MAP[inv.status] || STATUS_MAP.draft;
            const isOverdue = inv.due_date && new Date(inv.due_date) < new Date() && inv.status !== 'paid' && inv.status !== 'cancelled';
            const partnerName = inv.partner_snapshot_json?.name || inv.partner_name || '—';

            return (
              <div key={inv.id} style={{ borderRadius: 'var(--r)' }}>
              <Card style={{ borderLeft: isOverdue ? '3px solid var(--red)' : undefined, cursor: 'pointer' }} onClick={() => setShowDetail(inv)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--navy)', fontFamily: "'Plus Jakarta Sans', sans-serif", textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                      {inv.type === 'cancellation' && <span title="Sztornó számla" style={{ marginRight: '4px' }}>&#x1F6AB;</span>}
                      {inv.type === 'corrective' && <span title="Helyesbítő számla" style={{ marginRight: '4px' }}>&#x270F;&#xFE0F;</span>}
                      {inv.is_cancelled && inv.type !== 'cancellation' && inv.type !== 'corrective' && <span title="Sztornózva/Helyesbítve" style={{ marginRight: '4px', opacity: 0.5 }}>&#x274C;</span>}
                      <span style={{ textDecoration: inv.is_cancelled ? 'line-through' : undefined, opacity: inv.is_cancelled ? 0.6 : 1 }}>{inv.invoice_number || 'Vázlat'}</span>
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--slate)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {inv.partner_network_tenant_id && <ModulumBadge />}{partnerName}
                    </div>
                    {/* Kapcsolódó számla linkek */}
                    {inv.is_cancelled && inv.cancellation_invoice_number && (
                      <div style={{ fontSize: '11px', marginTop: '3px' }} onClick={e => e.stopPropagation()}>
                        <span style={{ color: 'var(--red)' }}>{inv.cancellation_invoice_type === 'corrective' ? '\u270F\uFE0F Helyesbítve' : '\uD83D\uDEAB Sztornózva'}: </span>
                        <button onClick={() => { const linked = invoices.find(i => i.id === inv.cancellation_invoice_id); if (linked) setShowDetail(linked); }}
                          style={{ color: 'var(--orange)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontSize: '11px' }}>
                          {inv.cancellation_invoice_number}
                        </button>
                      </div>
                    )}
                    {(inv.type === 'cancellation' || inv.type === 'corrective') && inv.referenced_invoice_number && (
                      <div style={{ fontSize: '11px', marginTop: '3px' }} onClick={e => e.stopPropagation()}>
                        <span style={{ color: 'var(--slate)' }}>Eredeti számla: </span>
                        <button onClick={() => { const linked = invoices.find(i => i.id === inv.referenced_invoice_id); if (linked) setShowDetail(linked); }}
                          style={{ color: 'var(--orange)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontSize: '11px' }}>
                          {inv.referenced_invoice_number}
                        </button>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }} onClick={e => e.stopPropagation()}>
                    <Badge variant={st.variant}>{st.label}</Badge>
                    <Badge variant={getTypeVariant(inv)}>{getTypeLabel(inv)}</Badge>
                    <ActionsDropdown actions={[
                      { label: 'Részletek', icon: '👁', onClick: () => setShowDetail(inv) },
                      { label: 'PDF letöltés', icon: '📥', onClick: async () => {
                        setGlobalLoading(true);
                        try { await downloadFile(`/invoices/${inv.id}/pdf`, `${inv.invoice_number || ('vazlat-' + inv.id.slice(0,8))}.pdf`); } catch (e: any) { toast('PDF letöltés sikertelen.', 'error'); }
                        finally { setGlobalLoading(false); }
                      } },
                      ...(inv.status !== 'draft' ? [{ label: 'NAV XML letöltés', icon: '📋', onClick: async () => {
                        try { await downloadFile(`/invoices/${inv.id}/nav-xml`, `${inv.invoice_number || ('vazlat-' + inv.id.slice(0,8))}-nav.xml`); } catch (e: any) { toast('XML letöltés sikertelen.', 'error'); }
                      } }] : []),
                      ...(inv.status === 'draft' ? [{ label: 'Kiállítás', icon: '📄', onClick: async () => {
                        try {
                          const result = await post<any>(`/invoices/${inv.id}/issue`, {});
                          loadInvoices();
                          setPostIssue({
                            id: inv.id,
                            number: result?.invoice_number || inv.invoice_number || '',
                            type: result?.type || inv.type,
                            partnerId: result?.partner_id || inv.partner_id || '',
                            partnerName: partnerName,
                            isCash: result?.payment_method === 'cash',
                          });
                        } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
                      } }] : []),
                      ...(inv.status === 'issued' ? [{ label: 'Fizetés rögzítése', icon: '💰', onClick: () => setShowPayment(inv) }] : []),
                      ...((inv.status === 'issued' || inv.status === 'paid') && inv.type !== 'cancellation' && inv.type !== 'corrective' && !inv.is_cancelled ? [{ label: 'Sztornó számla', icon: '↩', onClick: async () => {
                        if (!confirm('Biztosan sztornózod ezt a számlát? Ez egy új sztornó számlát hoz létre.')) return;
                        try { await post(`/invoices/${inv.id}/cancel`, { reason: 'Sztornózás' }); toast('Sztornó számla létrehozva.', 'success'); loadInvoices(); } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
                      } }] : []),
                      ...((inv.status === 'issued' || inv.status === 'paid') && inv.type !== 'cancellation' && inv.type !== 'corrective' && !inv.is_cancelled ? [{ label: 'Helyesbítő számla', icon: '✏', onClick: () => {
                        setShowCorrective(inv);
                      } }] : []),
                      ...(inv.type === 'proforma' ? [{ label: 'Számlává alakítás', icon: '📄', onClick: async () => {
                        try { await post(`/invoices/${inv.id}/convert-from-proforma`, {}); toast('Díjbekérő számlává alakítva.', 'success'); loadInvoices(); } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
                      } }] : []),

                    ]} />
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '12px', color: 'var(--slate)' }}>
                  <span>Kibocsátás: {formatDate(inv.issue_date)}</span>
                  <span>Teljesítés: {formatDate(inv.performance_date)}</span>
                  <span>Esedékesség: {formatDate(inv.due_date)}</span>
                  <span style={{ marginLeft: 'auto', fontWeight: 700, fontSize: '14px', color: 'var(--navy)' }}>{formatPrice(inv.subtotal_gross)}</span>
                </div>
              </Card>
              </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="btn btn-ghost" style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--line)', borderRadius: 'var(--r)', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.5 : 1 }}>← Előző</button>
          <span style={{ fontSize: '13px', color: 'var(--slate)' }}>{page} / {totalPages} ({totalCount} db)</span>
          <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="btn btn-ghost" style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--line)', borderRadius: 'var(--r)', cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.5 : 1 }}>Következő →</button>
          <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }} className="form-input" style={{ width: '70px', fontSize: '12px' }}>
            <option value="25">25</option><option value="50">50</option><option value="100">100</option>
          </select>
        </div>
      )}

      {showEditor && <InvoiceEditor orderId={editorOrderId} preSelectedPartner={editorPreselectedPartner} onClose={() => { setShowEditor(false); setEditorOrderId(null); setEditorPreselectedPartner(null); }} onSaved={(issued) => {
        setShowEditor(false); setEditorOrderId(null); loadInvoices();
        if (issued) setPostIssue(issued);
      }} />}
      {showDetail && <InvoiceDetailModal invoice={showDetail} onClose={() => setShowDetail(null)} />}
      {showPayment && <PaymentModal invoice={showPayment} onClose={() => setShowPayment(null)} onDone={() => { setShowPayment(null); loadInvoices(); }} />}
      <LogoUploadModal isOpen={showLogoUpload} onClose={() => setShowLogoUpload(false)} />
      {showCorrective && <CorrectiveInvoiceModal sourceInvoice={showCorrective} onClose={() => setShowCorrective(null)} onDone={() => { setShowCorrective(null); loadInvoices(); }} />}

      {postIssue && (
        <PostIssueModal
          isOpen={true}
          onClose={() => setPostIssue(null)}
          docType={postIssue.type === 'proforma' ? 'proforma' : 'invoice'}
          docId={postIssue.id}
          docNumber={postIssue.number}
          partnerId={postIssue.partnerId}
          partnerName={postIssue.partnerName}
          isCash={postIssue.isCash}
        />
      )}
    </div>
  );
}

// ── Számla részletek modal ──
interface InvoiceLine {
  item_name_snapshot: string;
  quantity: number;
  unit: string;
  unit_price_net: number;
  discount_percent: number;
  vat_rate: string;
  line_net: number;
  line_vat: number;
  line_gross: number;
  konfig_params_display?: { nev: string; ertek: string | number }[];
}

function InvoiceDetailModal({ invoice, onClose }: { invoice: Invoice; onClose: () => void }) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [lines, setLines] = useState<InvoiceLine[]>([]);
  const [loadingLines, setLoadingLines] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const full = await get<any>(`/invoices/${invoice.id}`);
        setLines((full.lines || []).map((l: any) => ({
          item_name_snapshot: l.item_name_snapshot || 'Tétel',
          quantity: Number(l.quantity),
          unit: l.unit || 'db',
          unit_price_net: Number(l.unit_price_net),
          discount_percent: Number(l.discount_percent || 0),
          vat_rate: l.vat_rate || '27',
          line_net: Number(l.line_net),
          line_vat: Number(l.line_vat),
          line_gross: Number(l.line_gross),
          konfig_params_display: l.konfig_params_display || undefined,
        })));
      } catch { /* silent */ }
      setLoadingLines(false);
    })();
  }, [invoice.id]);

  return (
    <Modal isOpen={true} onClose={onClose} title={invoice.type === 'cancellation' ? `${invoice.invoice_number} — Sztornó számla` : invoice.type === 'corrective' ? `${invoice.invoice_number} — Helyesbítő számla` : `Számla: ${invoice.invoice_number || 'Vázlat'}`}>
      <div className="space-y-4">
        <div className="row-li"><div className="ri-main"><div className="ri-t">Partner</div><div className="ri-s">{invoice.partner_snapshot_json?.name || invoice.partner_name || '—'}</div></div></div>
        <div className="row-li"><div className="ri-main"><div className="ri-t">Típus</div><div className="ri-s">{getTypeLabel(invoice)}</div></div></div>
        <div className="row-li"><div className="ri-main"><div className="ri-t">Státusz</div><div className="ri-s">{STATUS_MAP[invoice.status]?.label || invoice.status}</div></div></div>
        <div className="row-li"><div className="ri-main"><div className="ri-t">Kibocsátás</div><div className="ri-s">{formatDate(invoice.issue_date)}</div></div></div>
        <div className="row-li"><div className="ri-main"><div className="ri-t">Teljesítés</div><div className="ri-s">{formatDate(invoice.performance_date)}</div></div></div>
        <div className="row-li"><div className="ri-main"><div className="ri-t">Esedékesség</div><div className="ri-s">{formatDate(invoice.due_date)}</div></div></div>

        {/* Tételsorok */}
        {loadingLines ? (
          <div style={{ fontSize: '12px', color: 'var(--slate)', padding: '8px 0' }}>Tételek betöltése...</div>
        ) : lines.length > 0 ? (
          <div>
            <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--navy)', marginBottom: '6px' }}>Tételek</div>
            <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r)', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              {lines.map((l, i) => (
                <div key={i} style={{ display: 'flex', gap: '8px', padding: '8px 10px', borderBottom: i < lines.length - 1 ? '1px solid var(--line-2)' : undefined, fontSize: '12px', color: 'var(--navy)' }}>
                  <div style={{ flex: 2, minWidth: 0 }}>
                    <div>{l.item_name_snapshot}</div>
                    {l.konfig_params_display && l.konfig_params_display.length > 0 && (
                      <div style={{ fontSize: '10px', color: 'var(--slate)', marginTop: '2px', display: 'flex', flexWrap: 'wrap', gap: '2px 8px' }}>
                        {l.konfig_params_display.map((p, pi) => (
                          <span key={pi}><span style={{ fontWeight: 500 }}>{p.nev}:</span> {p.ertek}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <span style={{ width: '70px', textAlign: 'right', color: 'var(--slate)' }}>{l.quantity} {l.unit}</span>
                  <span style={{ width: '80px', textAlign: 'right', color: 'var(--slate)' }}>{formatPrice(l.unit_price_net)}</span>
                  {l.discount_percent > 0 && <span style={{ width: '40px', textAlign: 'right', color: 'var(--red)' }}>-{l.discount_percent}%</span>}
                  <span style={{ width: '40px', textAlign: 'right', color: 'var(--slate)' }}>{l.vat_rate}%</span>
                  <span style={{ width: '90px', textAlign: 'right', fontWeight: 600 }}>{formatPrice(l.line_gross)}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="row-li"><div className="ri-main"><div className="ri-t">Nettó</div><div className="ri-s">{formatPrice(invoice.subtotal_net)}</div></div></div>
        <div className="row-li"><div className="ri-main"><div className="ri-t">ÁFA</div><div className="ri-s">{formatPrice(invoice.subtotal_vat)}</div></div></div>
        <div className="row-li"><div className="ri-main"><div className="ri-t">Bruttó</div><div className="ri-s" style={{ fontWeight: 700, fontSize: '16px', color: 'var(--navy)' }}>{formatPrice(invoice.subtotal_gross)}</div></div></div>
        <div className="row-li"><div className="ri-main"><div className="ri-t">Fizetési mód</div><div className="ri-s">{PAYMENT_METHOD_LABELS[invoice.payment_method || ''] || invoice.payment_method || '—'}</div></div></div>
        {invoice.currency && invoice.currency !== 'HUF' && <div className="row-li"><div className="ri-main"><div className="ri-t">Pénznem</div><div className="ri-s">{invoice.currency}</div></div></div>}
        {invoice.paid_amount && Number(invoice.paid_amount) > 0 && <div className="row-li"><div className="ri-main"><div className="ri-t">Befizetve</div><div className="ri-s" style={{ color: 'var(--green)' }}>{formatPrice(invoice.paid_amount)}</div></div></div>}
        <div className="row-li"><div className="ri-main"><div className="ri-t">NAV</div><div className="ri-s">{NAV_ICONS[invoice.nav_status || 'off']} {invoice.nav_status || 'Nincs beküldve'}</div></div></div>
        {invoice.notes && <div className="row-li"><div className="ri-main"><div className="ri-t">Megjegyzés</div><div className="ri-s">{invoice.notes}</div></div></div>}
        <div style={{ display: 'flex', gap: '9px', justifyContent: 'flex-end', paddingTop: '8px', flexWrap: 'wrap' }}>
          {invoice.partner_id && <Button variant="ghost" onClick={() => navigate(`/partnerek/${invoice.partner_id}`)}>Partner →</Button>}
          <Button variant="ghost" onClick={async () => { try { await downloadFile(`/invoices/${invoice.id}/pdf`, `${invoice.invoice_number || ('vazlat-' + invoice.id.slice(0,8))}.pdf`); } catch { toast('PDF letöltés sikertelen.', 'error'); } }}>PDF letöltés</Button>
          <Button variant="ghost" onClick={onClose}>Bezárás</Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Fizetés rögzítés modal ──
function PaymentModal({ invoice, onClose, onDone }: { invoice: Invoice; onClose: () => void; onDone: () => void }) {
  const { toast } = useToast();
  const [amount, setAmount] = useState(Math.round(Number(invoice.subtotal_gross)));
  const [method, setMethod] = useState('transfer');
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await post(`/invoices/${invoice.id}/payments`, { amount, paidAt: new Date().toISOString().slice(0, 10), method });
      toast('Fizetés rögzítve.', 'success');
      onDone();
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); } finally { setSaving(false); }
  }

  return (
    <Modal isOpen={true} onClose={onClose} title="Fizetés rögzítése">
      <div className="space-y-4">
        <div style={{ fontSize: '13px', color: 'var(--slate)' }}>Számla: <strong>{invoice.invoice_number}</strong> — {formatPrice(invoice.subtotal_gross)}</div>
        <MoneyInput label="Összeg (Ft) *" value={amount} onChange={setAmount} />
        <div>
          <label className="form-label">Fizetési mód</label>
          <select value={method} onChange={e => setMethod(e.target.value)} className="form-input">
            <option value="transfer">Átutalás</option>
            <option value="cash">Készpénz</option>
            <option value="card">Bankkártya</option>
          </select>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '9px', paddingTop: '8px' }}>
          <Button variant="ghost" onClick={onClose}>Mégse</Button>
          <Button onClick={handleSave} loading={saving}>Rögzítés</Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Számla szerkesztő modal — előleg + végszámla támogatás ──

type InvoiceKind = 'normal' | 'advance' | 'final';
interface LineDraft { itemName: string; quantity: number; unitPriceNet: number; vatRate: string; advanceIndicator?: boolean; readonly?: boolean; konfigParamsDisplay?: { nev: string; ertek: string | number }[]; }

function InvoiceEditor({ orderId, preSelectedPartner, onClose, onSaved }: { orderId?: string | null; preSelectedPartner?: { id: string; name: string } | null; onClose: () => void; onSaved: (issued?: { id: string; number: string; type: string; partnerId: string; partnerName: string; isCash?: boolean }) => void }) {
  const { toast } = useToast();
  const [selectedPartner, setSelectedPartner] = useState<{ id: string; name: string } | null>(null);
  const [partnerPriceListId, setPartnerPriceListId] = useState<string | null>(null);

  function handlePartnerChange(p: { id: string; name: string } | null) {
    setSelectedPartner(p);
    setPartnerPriceListId(null);
    if (p) {
      get<any>(`/partners/${p.id}`).then(data => setPartnerPriceListId(data?.price_list_id || null)).catch(() => {});
    }
  }
  const [invoiceKind, setInvoiceKind] = useState<InvoiceKind>('normal');
  const [type, setType] = useState('normal'); // normal / proforma
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0, 10));
  const [performanceDate, setPerformanceDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('transfer');
  const [currency, setCurrency] = useState('HUF');
  const [exchangeRate, setExchangeRate] = useState(1);
  const [exchangeRateSource, setExchangeRateSource] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([{ itemName: '', quantity: 1, unitPriceNet: 0, vatRate: '27' }]);
  const [saving, setSaving] = useState(false);
  const [confirmIssue, setConfirmIssue] = useState(true); // Alapértelmezetten kiállítjuk

  // Előleg-specifikus
  const [advanceMode, setAdvanceMode] = useState<'percent' | 'fixed'>('percent');
  const [advancePercent, setAdvancePercent] = useState(50);
  const [advanceFixedNet, setAdvanceFixedNet] = useState(0);
  const [advanceVatRate, setAdvanceVatRate] = useState('27');
  const [advanceDescription, setAdvanceDescription] = useState('Előleg');
  const [advanceOrderId, setAdvanceOrderId] = useState('');
  const [partnerOrders, setPartnerOrders] = useState<any[]>([]);

  // Végszámla: partner előlegszámlái
  const [advanceInvoices, setAdvanceInvoices] = useState<AdvanceInvoice[]>([]);
  const [selectedAdvanceIds, setSelectedAdvanceIds] = useState<string[]>([]);
  const [loadingAdvances, setLoadingAdvances] = useState(false);

  // Megrendelésből érkező előkitöltés
  useEffect(() => {
    if (!orderId) return;
    (async () => {
      try {
        const order = await get<any>(`/sales-orders/${orderId}`);
        if (order) {
          // Partner beállítása
          const pName = order.partner_name || order.partner_snapshot_json?.name || '';
          if (order.partner_id) {
            setSelectedPartner({ id: order.partner_id, name: pName });
            get<any>(`/partners/${order.partner_id}`).then(p => setPartnerPriceListId(p?.price_list_id || null)).catch(() => {});
          }
          // Tételsorok átvétele
          const orderLines = order.lines || order.line_items || [];
          if (orderLines.length > 0) {
            setLines(orderLines.map((l: any) => ({
              itemName: l.item_name_snapshot || l.description || 'Tétel',
              quantity: Number(l.quantity),
              unitPriceNet: Math.round(Number(l.unit_price_net || l.unitPriceNet || 0)),
              vatRate: l.vat_rate || '27',
              konfigParamsDisplay: l.konfig_params_display || undefined,
            })));
          }
        }
      } catch { /* silent */ }
    })();
  }, [orderId]);

  // Előre kiválasztott partner (pl. partnertől navigálva)
  useEffect(() => {
    if (preSelectedPartner && !orderId) { handlePartnerChange(preSelectedPartner); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Készpénzes fizetésnél teljesítés = kibocsátás, esedékesség = kibocsátás
  useEffect(() => {
    if (paymentMethod === 'cash') { setPerformanceDate(issueDate); setDueDate(issueDate); }
  }, [paymentMethod, issueDate]);

  // Default megjegyzés betöltése a cégprofilból (típustól függően)
  useEffect(() => {
    (async () => {
      try {
        const profile = await get<any>('/company-profile');
        if (type === 'proforma' && profile?.default_proforma_notes) {
          setNotes(profile.default_proforma_notes);
        } else if (profile?.default_invoice_notes) {
          setNotes(profile.default_invoice_notes);
        }
      } catch { /* silent */ }
    })();
  }, [type]);

  // Előlegszámlák betöltése ha partnert választunk és végszámla mód
  useEffect(() => {
    if (!selectedPartner) { setAdvanceInvoices([]); return; }
    (async () => {
      setLoadingAdvances(true);
      try {
        const list = await get<AdvanceInvoice[]>(`/invoices/by-partner/${selectedPartner.id}/advances`);
        setAdvanceInvoices(Array.isArray(list) ? list : []);
      } catch { setAdvanceInvoices([]); }
      setLoadingAdvances(false);
    })();
  }, [selectedPartner?.id]);

  // Ha partnert váltunk, resetteljük
  useEffect(() => {
    setSelectedAdvanceIds([]);
    setInvoiceKind('normal');
  }, [selectedPartner?.id]);

  // Partner megrendeléseinek betöltése (előlegszámla kötéséhez)
  useEffect(() => {
    if (!selectedPartner) { setPartnerOrders([]); return; }
    (async () => {
      try {
        const d = await get<any>(`/sales-orders?partnerId=${selectedPartner.id}`);
        setPartnerOrders(Array.isArray(d) ? d : d?.data || []);
      } catch { setPartnerOrders([]); }
    })();
  }, [selectedPartner?.id]);

  // Ha nincs előleg de végszámla volt kiválasztva, reseteljük
  useEffect(() => {
    if (advanceInvoices.length === 0 && invoiceKind === 'final') setInvoiceKind('normal');
  }, [advanceInvoices.length]);

  function addLine() { setLines(prev => [...prev, { itemName: '', quantity: 1, unitPriceNet: 0, vatRate: '27' }]); }

  // Előlegszámla nettó összeg kiszámítása
  const advanceNetAmount = advanceMode === 'percent'
    ? Math.round(lines.reduce((s, l) => s + l.quantity * l.unitPriceNet, 0) * advancePercent / 100)
    : advanceFixedNet;
  const advanceVatAmount = Math.round(advanceNetAmount * Number(advanceVatRate) / 100);
  const advanceGrossAmount = advanceNetAmount + advanceVatAmount;

  // Normál + végszámla összesítő
  const lineTotals = lines.filter(l => !l.readonly).reduce((s, l) => {
    const net = l.quantity * l.unitPriceNet;
    const vat = Math.round(net * Number(l.vatRate) / 100);
    return { net: s.net + net, vat: s.vat + vat, gross: s.gross + net + vat };
  }, { net: 0, vat: 0, gross: 0 });

  // Végszámla: levonandó előlegek összege
  const selectedAdvances = advanceInvoices.filter(a => selectedAdvanceIds.includes(a.id));
  const advanceDeductionNet = selectedAdvances.reduce((s, a) => s + a.subtotal_net, 0);
  const advanceDeductionVat = selectedAdvances.reduce((s, a) => s + a.subtotal_vat, 0);
  const advanceDeductionGross = selectedAdvances.reduce((s, a) => s + a.subtotal_gross, 0);

  // Végszámla fizetendő
  const finalNet = lineTotals.net - advanceDeductionNet;
  const finalVat = lineTotals.vat - advanceDeductionVat;
  const finalGross = lineTotals.gross - advanceDeductionGross;

  function toggleAdvanceSelection(id: string) {
    setSelectedAdvanceIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  async function handleSave() {
    if (!selectedPartner) { toast('Válassz partnert.', 'error'); return; }
    if (currency !== 'HUF' && exchangeRate <= 1) { toast('Devizás számlához adj meg érvényes árfolyamot (> 1).', 'error'); return; }
    if (currency !== 'HUF' && !exchangeRateSource.trim()) { toast('Devizás számlához add meg az árfolyam forrását.', 'error'); return; }

    if (invoiceKind === 'advance') {
      // Előlegszámla: egy tétel az előleg összegével
      if (advanceNetAmount <= 0) { toast('Az előleg összege nem lehet nulla.', 'error'); return; }
      setSaving(true);
      try {
        const result = await post<any>('/invoices', {
          partnerId: selectedPartner.id, type, issueDate, performanceDate,
          dueDate: dueDate || undefined, paymentMethod, notes: notes || undefined,
          currency, exchangeRate: currency !== 'HUF' ? exchangeRate : 1, exchangeRateSource: currency !== 'HUF' ? exchangeRateSource : undefined,
          isAdvance: true, autoIssue: confirmIssue,
          priceListId: partnerPriceListId || undefined,
          salesOrderId: advanceOrderId || undefined,
          lines: [{
            itemName: advanceDescription || 'Előleg',
            quantity: 1,
            unitPriceNet: advanceNetAmount,
            vatRate: advanceVatRate,
            advanceIndicator: true,
          }],
        });
        if (confirmIssue && result?.id) {
          onSaved({ id: result.id, number: result.invoice_number || '', type: result.type || type, partnerId: selectedPartner.id, partnerName: selectedPartner.name, isCash: paymentMethod === 'cash' });
        } else {
          toast(confirmIssue ? 'Előlegszámla kiállítva.' : 'Előlegszámla létrehozva (vázlat).', 'success');
          onSaved();
        }
      } catch (e: any) { toast(e.message || 'Mentés sikertelen.', 'error'); } finally { setSaving(false); }
      return;
    }

    if (invoiceKind === 'final') {
      // Végszámla: összes tétel + negatív előleg tételek
      if (!lines.some(l => l.itemName)) { toast('Adj hozzá legalább egy tételt.', 'error'); return; }
      if (selectedAdvanceIds.length === 0) { toast('Válassz ki legalább egy előlegszámlát a beszámításhoz.', 'error'); return; }
      setSaving(true);
      try {
        const invoiceLines: any[] = [];
        // Normál tételek
        for (const l of lines.filter(l => l.itemName)) {
          invoiceLines.push({ itemName: l.itemName, quantity: l.quantity, unitPriceNet: l.unitPriceNet, vatRate: l.vatRate });
        }
        // Előleg-beszámítás tételek (negatív) — ÁFA-kulcsonként bontva az eredeti előlegszámla szerint
        // Előleg-beszámítás: a TÁROLT nettó/ÁFA/bruttó értékeket negáljuk (nem újraszámolás!)
        for (const adv of selectedAdvances) {
          const advLines = adv.lines || [];
          if (advLines.length === 0) {
            invoiceLines.push({
              itemName: `Előleg beszámítás (${adv.invoice_number})`,
              quantity: -1, unitPriceNet: adv.subtotal_net,
              vatRate: '27', advanceIndicator: true,
              lineVatOverride: -adv.subtotal_vat,
              lineGrossOverride: -adv.subtotal_gross,
            });
          } else {
            for (const al of advLines) {
              invoiceLines.push({
                itemName: `Előleg beszámítás (${adv.invoice_number})`,
                quantity: -1, unitPriceNet: al.lineNet,
                vatRate: al.vatRate, advanceIndicator: true,
                lineVatOverride: -al.lineVat,
                lineGrossOverride: -al.lineGross,
              });
            }
          }
        }
        const result = await post<any>('/invoices', {
          partnerId: selectedPartner.id, type, issueDate, performanceDate,
          dueDate: dueDate || undefined, paymentMethod, notes: notes || undefined,
          currency, exchangeRate: currency !== 'HUF' ? exchangeRate : 1, exchangeRateSource: currency !== 'HUF' ? exchangeRateSource : undefined,
          advanceSourceInvoiceIds: selectedAdvanceIds, autoIssue: confirmIssue,
          priceListId: partnerPriceListId || undefined,
          lines: invoiceLines,
        });
        if (confirmIssue && result?.id) {
          onSaved({ id: result.id, number: result.invoice_number || '', type: result.type || type, partnerId: selectedPartner!.id, partnerName: selectedPartner!.name, isCash: paymentMethod === 'cash' });
        } else {
          toast(confirmIssue ? 'Végszámla kiállítva.' : 'Végszámla létrehozva (vázlat).', 'success');
          onSaved();
        }
      } catch (e: any) { toast(e.message || 'Mentés sikertelen.', 'error'); } finally { setSaving(false); }
      return;
    }

    // Normál számla
    if (!lines.some(l => l.itemName)) { toast('Adj hozzá legalább egy tételt.', 'error'); return; }
    setSaving(true);
    try {
      const result = await post<any>('/invoices', {
        partnerId: selectedPartner.id, type, issueDate, performanceDate,
        dueDate: dueDate || undefined, paymentMethod, notes: notes || undefined,
        currency, exchangeRate: currency !== 'HUF' ? exchangeRate : 1, exchangeRateSource: currency !== 'HUF' ? exchangeRateSource : undefined,
        autoIssue: confirmIssue,
        priceListId: partnerPriceListId || undefined,
        lines: lines.filter(l => l.itemName).map(l => ({
          itemName: l.itemName, quantity: l.quantity,
          unitPriceNet: l.unitPriceNet, vatRate: l.vatRate,
          konfigParamsDisplay: l.konfigParamsDisplay || undefined,
        })),
      });
      if (confirmIssue && result?.id) {
        onSaved({ id: result.id, number: result.invoice_number || '', type: result.type || type, partnerId: selectedPartner.id, partnerName: selectedPartner.name });
      } else {
        toast(confirmIssue ? 'Számla kiállítva.' : 'Számla létrehozva (vázlat).', 'success');
        onSaved();
      }
    } catch (e: any) { toast(e.message || 'Mentés sikertelen.', 'error'); } finally { setSaving(false); }
  }

  // Előlegszámlák banner — ha a partner rendelkezik előlegszámlákkal (kiállított, nem sztornózott)
  const hasAdvances = advanceInvoices.length > 0 && !!selectedPartner;

  return (
    <Modal isOpen={true} onClose={onClose} title="+ Új számla" size="lg"
      footer={<>
        <Button variant="ghost" onClick={onClose}>Mégse</Button>
        <Button onClick={handleSave} loading={saving}>
          {confirmIssue
            ? (invoiceKind === 'advance' ? 'Előlegszámla kiállítása' : invoiceKind === 'final' ? 'Végszámla kiállítása' : 'Számla kiállítása')
            : (invoiceKind === 'advance' ? 'Előlegszámla mentése (vázlat)' : invoiceKind === 'final' ? 'Végszámla mentése (vázlat)' : 'Számla mentése (vázlat)')}
        </Button>
      </>}>
      <div className="space-y-4">
        <PartnerAutocomplete label="Partner" value={selectedPartner} onChange={handlePartnerChange} required placeholder="Keress partnert..." />

        {/* Előlegszámla banner — jelzi ha vannak előlegszámlák */}
        {hasAdvances && (
          <div style={{ background: 'var(--orange-soft)', padding: '12px 14px', borderRadius: 'var(--r)', fontSize: '13px' }}>
            <div style={{ fontWeight: 700, color: 'var(--orange-deep)', marginBottom: '4px' }}>
              {advanceInvoices.length} előlegszámla van a rendszerben ehhez a partnerhez
            </div>
            <div style={{ color: 'var(--slate)', fontSize: '12px' }}>
              Végszámlánál automatikusan beszámítjuk az előlegeket.
            </div>
          </div>
        )}

        {/* Számla jellege: mindig 3 opció, végszámla csak ha van előleg */}
        <div>
          <label className="form-label">Számla jellege</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '8px' }}>
            {[
              { key: 'normal' as const, label: 'Normál számla', desc: 'Teljes összeg' },
              { key: 'advance' as const, label: 'Előlegszámla', desc: 'Részösszeg bekérése' },
              ...(hasAdvances ? [{ key: 'final' as const, label: 'Végszámla', desc: `Előlegek levonva (${advanceInvoices.length})` }] : []),
            ].map(opt => (
              <button key={opt.key} onClick={() => setInvoiceKind(opt.key)}
                style={{
                  padding: '10px 12px', borderRadius: 'var(--r)', border: invoiceKind === opt.key ? '2px solid var(--orange)' : '1px solid var(--line)',
                  background: invoiceKind === opt.key ? 'var(--orange-soft)' : 'var(--white)',
                  cursor: 'pointer', textAlign: 'left',
                }}>
                <div style={{ fontWeight: 700, fontSize: '13px', color: invoiceKind === opt.key ? 'var(--orange-deep)' : 'var(--navy)' }}>{opt.label}</div>
                <div style={{ fontSize: '11px', color: 'var(--slate)', marginTop: '2px' }}>{opt.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px' }}>
          <div><label className="form-label">Típus</label>
            <select value={type} onChange={e => setType(e.target.value)} className="form-input">
              <option value="normal">Normál</option><option value="proforma">Díjbekérő</option>
            </select>
          </div>
          <div><label className="form-label">Fizetési mód</label>
            <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} className="form-input">
              <option value="transfer">Átutalás</option><option value="cash">Készpénz</option><option value="card">Bankkártya</option>
            </select>
          </div>
          <div><label className="form-label">Pénznem</label>
            <select value={currency} onChange={e => { setCurrency(e.target.value); if (e.target.value === 'HUF') { setExchangeRate(1); setExchangeRateSource(''); } }} className="form-input">
              <option value="HUF">HUF</option><option value="EUR">EUR</option><option value="USD">USD</option><option value="GBP">GBP</option><option value="CHF">CHF</option><option value="CZK">CZK</option><option value="RON">RON</option>
            </select>
          </div>
        </div>

        {currency !== 'HUF' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Input label={`Árfolyam (1 ${currency} = ? HUF) *`} type="number" value={String(exchangeRate)} onChange={e => setExchangeRate(Number(e.target.value))} />
            <Input label="Árfolyam forrása *" value={exchangeRateSource} onChange={e => setExchangeRateSource(e.target.value)} placeholder="MNB középárfolyam" />
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px' }}>
          <Input label="Kibocsátás *" type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)} />
          <Input label="Teljesítés *" type="date" value={performanceDate} onChange={e => setPerformanceDate(e.target.value)} disabled={paymentMethod === 'cash'} />
          <Input label="Esedékesség" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} disabled={paymentMethod === 'cash'} />
        </div>

        {paymentMethod === 'cash' && <div className="onb-info">Készpénzes fizetésnél a teljesítés és esedékesség automatikusan a kibocsátás napja.</div>}

        {/* ═══════ TÉTELEK ═══════ */}
        <div>
          <label className="form-label">Tételek</label>
          {lines.map((l, i) => {
            const lineNet = Math.round(l.quantity * l.unitPriceNet);
            const lineVat = Math.round(lineNet * Number(l.vatRate) / 100);
            return (
              <div key={i} style={{ border: '1px solid var(--line)', borderRadius: 'var(--r)', padding: '10px 12px', marginBottom: '8px', background: 'var(--white)' }}>
                {/* Tétel név + törlés */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                  <input value={l.itemName} onChange={e => { const n = [...lines]; n[i].itemName = e.target.value; setLines(n); }} placeholder="Tétel neve *" className="form-input" style={{ flex: 1 }} />
                  <button onClick={() => setLines(lines.filter((_, j) => j !== i))} style={{ color: 'var(--red)', border: 'none', background: 'none', cursor: 'pointer', fontSize: '18px', padding: '2px 6px', flexShrink: 0 }}>×</button>
                </div>
                {/* Mennyiség, egységár, ÁFA — mobilon is elfér */}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: '60px' }}>
                    <label style={{ fontSize: '10px', color: 'var(--slate)', display: 'block', marginBottom: '2px' }}>Menny.</label>
                    <NumInput value={l.quantity} onChange={v => { const n = [...lines]; n[i].quantity = v; setLines(n); }} min={0.001} className="form-input" style={{ width: '60px', textAlign: 'center' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: '90px' }}>
                    <label style={{ fontSize: '10px', color: 'var(--slate)', display: 'block', marginBottom: '2px' }}>Nettó egységár</label>
                    <MoneyInput value={l.unitPriceNet} onChange={v => { const n = [...lines]; n[i].unitPriceNet = v; setLines(n); }} placeholder="0" />
                  </div>
                  <div style={{ minWidth: '55px' }}>
                    <label style={{ fontSize: '10px', color: 'var(--slate)', display: 'block', marginBottom: '2px' }}>ÁFA</label>
                    <select value={l.vatRate} onChange={e => { const n = [...lines]; n[i].vatRate = e.target.value; setLines(n); }} className="form-input" style={{ width: '60px' }}>
                      <option value="27">27%</option><option value="18">18%</option><option value="5">5%</option><option value="0">0%</option>
                    </select>
                  </div>
                  <div style={{ textAlign: 'right', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                    <div style={{ fontSize: '10px', color: 'var(--slate)' }}>Bruttó</div>
                    <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--navy)' }}>{(lineNet + lineVat).toLocaleString('hu-HU')} Ft</div>
                  </div>
                </div>
              </div>
            );
          })}
          <button onClick={addLine} style={{ fontSize: '13px', color: 'var(--orange)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>+ Új tétel</button>
        </div>

        {/* ═══════ ELŐLEGSZÁMLA BEÁLLÍTÁSOK ═══════ */}
        {invoiceKind === 'advance' && (
          <div style={{ border: '2px solid var(--orange)', borderRadius: 'var(--r)', padding: '14px' }}>
            <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--orange-deep)', marginBottom: '10px' }}>Előleg beállítások</div>

            {/* Megrendeléshez kötés */}
            {partnerOrders.length > 0 && (
              <div style={{ marginBottom: '10px' }}>
                <label className="form-label">Előleg melyik megrendeléshez tartozik? (opcionális)</label>
                <select value={advanceOrderId} onChange={e => setAdvanceOrderId(e.target.value)} className="form-input">
                  <option value="">— Nem kötöm megrendeléshez —</option>
                  {partnerOrders.map((o: any) => <option key={o.id} value={o.id}>{(o.order_number || '').toUpperCase()} — {Math.round(Number(o.subtotal_gross || 0)).toLocaleString('hu-HU')} Ft</option>)}
                </select>
                <div style={{ fontSize: '11px', color: 'var(--slate)', marginTop: '4px' }}>Ha kiválasztod, az előleg ehhez a megrendeléshez lesz kötve a végszámlánál.</div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
              <button onClick={() => setAdvanceMode('percent')}
                style={{ padding: '8px 12px', borderRadius: 'var(--r)', border: advanceMode === 'percent' ? '2px solid var(--orange)' : '1px solid var(--line)', background: advanceMode === 'percent' ? 'var(--orange-soft)' : 'var(--white)', cursor: 'pointer', fontWeight: 600, fontSize: '13px', color: advanceMode === 'percent' ? 'var(--orange-deep)' : 'var(--navy)' }}>
                Százalékos
              </button>
              <button onClick={() => setAdvanceMode('fixed')}
                style={{ padding: '8px 12px', borderRadius: 'var(--r)', border: advanceMode === 'fixed' ? '2px solid var(--orange)' : '1px solid var(--line)', background: advanceMode === 'fixed' ? 'var(--orange-soft)' : 'var(--white)', cursor: 'pointer', fontWeight: 600, fontSize: '13px', color: advanceMode === 'fixed' ? 'var(--orange-deep)' : 'var(--navy)' }}>
                Fix összeg
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '10px', marginBottom: '10px' }}>
              {advanceMode === 'percent' ? (
                <div>
                  <label className="form-label">Előleg %</label>
                  <NumInput value={advancePercent} onChange={v => setAdvancePercent(v)} min={1} max={99} className="form-input" />
                </div>
              ) : (
                <MoneyInput label="Előleg nettó összeg (Ft)" value={advanceFixedNet} onChange={setAdvanceFixedNet} />
              )}
              <div>
                <label className="form-label">ÁFA kulcs</label>
                <select value={advanceVatRate} onChange={e => setAdvanceVatRate(e.target.value)} className="form-input">
                  <option value="27">27%</option><option value="18">18%</option><option value="5">5%</option><option value="0">0%</option>
                </select>
              </div>
              <div>
                <label className="form-label">Megnevezés</label>
                <input value={advanceDescription} onChange={e => setAdvanceDescription(e.target.value)} className="form-input" placeholder="Előleg" />
              </div>
            </div>

            {/* Előleg összesítő */}
            <div style={{ background: 'var(--orange-soft)', borderRadius: 'var(--r)', padding: '10px 12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--slate)' }}><span>Előleg nettó</span><span>{formatPrice(advanceNetAmount)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--slate)' }}><span>ÁFA ({advanceVatRate}%)</span><span>{formatPrice(advanceVatAmount)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', fontWeight: 700, color: 'var(--orange-deep)', borderTop: '1px solid var(--line)', paddingTop: '6px', marginTop: '4px' }}><span>Előleg bruttó</span><span>{formatPrice(advanceGrossAmount)}</span></div>
            </div>

            {advanceMode === 'percent' && lineTotals.net === 0 && (
              <div style={{ fontSize: '12px', color: 'var(--red)', marginTop: '6px' }}>Add meg a tételeket a százalékos előleg számításához.</div>
            )}
          </div>
        )}

        {/* ═══════ VÉGSZÁMLA — ELŐLEG BESZÁMÍTÁS ═══════ */}
        {invoiceKind === 'final' && (
          <div style={{ border: '2px solid var(--green)', borderRadius: 'var(--r)', padding: '14px' }}>
            <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--navy)', marginBottom: '10px' }}>Előlegek beszámítása</div>

            {loadingAdvances ? (
              <div style={{ fontSize: '13px', color: 'var(--slate)' }}>Előlegszámlák betöltése...</div>
            ) : advanceInvoices.length === 0 ? (
              <div style={{ fontSize: '13px', color: 'var(--slate)', padding: '10px 0' }}>
                {selectedPartner ? 'Ehhez a partnerhez nincs kiállított előlegszámla.' : 'Válassz partnert az előlegszámlák megjelenítéséhez.'}
              </div>
            ) : (
              <>
                <div style={{ fontSize: '12px', color: 'var(--slate)', marginBottom: '8px' }}>Jelöld ki a beszámítandó <b>előlegszámlákat</b> (nem megrendeléseket!):</div>
                <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r)' }}>
                  {advanceInvoices.map(adv => {
                    const isSelected = selectedAdvanceIds.includes(adv.id);
                    return (
                      <label key={adv.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderBottom: '1px solid var(--line-2)', cursor: adv.usedInInvoice ? 'not-allowed' : 'pointer', background: isSelected ? 'var(--green-soft, #f0fdf4)' : adv.usedInInvoice ? '#f3f4f6' : undefined, opacity: adv.usedInInvoice ? 0.5 : 1 }}>
                        <input type="checkbox" checked={isSelected} onChange={() => !adv.usedInInvoice && toggleAdvanceSelection(adv.id)} disabled={!!adv.usedInInvoice} style={{ width: '18px', height: '18px', accentColor: 'var(--navy)' }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--navy)', textTransform: 'uppercase' }}>
                            {adv.invoice_number}
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--slate)' }}>Előlegszámla · {formatDate(adv.issue_date)}</div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--navy)' }}>{formatPrice(adv.subtotal_gross)}</div>
                          <div style={{ fontSize: '11px', color: 'var(--slate)' }}>nettó: {formatPrice(adv.subtotal_net)}</div>
                          {adv.usedInInvoice && <div style={{ fontSize: '11px', color: 'var(--red)' }}>Már beszámítva</div>}
                        </div>
                      </label>
                    );
                  })}
                </div>

                {/* Végszámla előnézet */}
                {selectedAdvanceIds.length > 0 && (
                  <div style={{ marginTop: '10px', padding: '10px 12px', background: 'var(--mist)', borderRadius: 'var(--r)', fontSize: '13px' }}>
                    <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: '6px' }}>Végszámla tételek előnézete:</div>
                    {lines.filter(l => l.itemName).map((l, i) => {
                      const lineNet = l.quantity * l.unitPriceNet;
                      const lineVat = Math.round(lineNet * Number(l.vatRate) / 100);
                      return (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', color: 'var(--navy)' }}>
                          <span>{l.itemName} ({l.quantity} db × {formatPrice(l.unitPriceNet)})</span>
                          <span style={{ fontWeight: 600 }}>{formatPrice(lineNet + lineVat)}</span>
                        </div>
                      );
                    })}
                    {selectedAdvances.map(adv => (
                      <div key={adv.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', color: 'var(--red)' }}>
                        <span>Előleg beszámítás ({adv.invoice_number}) — −1 db</span>
                        <span style={{ fontWeight: 600 }}>−{formatPrice(adv.subtotal_gross)}</span>
                      </div>
                    ))}
                    <div style={{ borderTop: '1px solid var(--line)', marginTop: '6px', paddingTop: '6px', display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '15px', color: finalGross >= 0 ? 'var(--navy)' : 'var(--red)' }}>
                      <span>Fizetendő</span>
                      <span>{formatPrice(finalGross)}</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ═══════ ÖSSZESÍTŐ ═══════ */}
        <div style={{ background: 'var(--mist)', borderRadius: 'var(--r)', padding: '12px' }}>
          {invoiceKind === 'advance' ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--slate)' }}><span>Előleg nettó</span><span>{formatPrice(advanceNetAmount)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--slate)' }}><span>ÁFA</span><span>{formatPrice(advanceVatAmount)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', fontWeight: 700, color: 'var(--navy)', borderTop: '1px solid var(--line)', paddingTop: '6px', marginTop: '4px' }}><span>Fizetendő</span><span>{formatPrice(advanceGrossAmount)}</span></div>
            </>
          ) : invoiceKind === 'final' && selectedAdvanceIds.length > 0 ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--slate)' }}><span>Tételek nettó</span><span>{formatPrice(lineTotals.net)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--slate)' }}><span>Tételek ÁFA</span><span>{formatPrice(lineTotals.vat)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--slate)' }}><span>Tételek bruttó</span><span>{formatPrice(lineTotals.gross)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--red)', fontWeight: 600, marginTop: '4px' }}><span>Előleg levonás ({selectedAdvanceIds.length} db)</span><span>−{formatPrice(advanceDeductionGross)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', fontWeight: 700, color: 'var(--navy)', borderTop: '1px solid var(--line)', paddingTop: '6px', marginTop: '4px' }}><span>Fizetendő</span><span>{formatPrice(finalGross)}</span></div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--slate)' }}><span>Nettó</span><span>{formatPrice(lineTotals.net)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--slate)' }}><span>ÁFA</span><span>{formatPrice(lineTotals.vat)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', fontWeight: 700, color: 'var(--navy)', borderTop: '1px solid var(--line)', paddingTop: '6px', marginTop: '4px' }}><span>Bruttó</span><span>{formatPrice(lineTotals.gross)}</span></div>
            </>
          )}
        </div>

        <div>
          <label className="form-label">Megjegyzés (megjelenik a számlán)</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="form-input" placeholder="Pl. Köszönjük a megrendelést!" />
          <div style={{ fontSize: '11px', color: 'var(--slate)', marginTop: '4px' }}>Állandó szöveget a Beállítások → Cég-profil oldalon tudsz beállítani.</div>
        </div>

        {/* Kiállítás megerősítés */}
        <div style={{ background: 'var(--mist)', borderRadius: 'var(--r)', padding: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <input type="checkbox" id="confirmIssue" checked={confirmIssue} onChange={e => setConfirmIssue(e.target.checked)} style={{ width: '18px', height: '18px' }} />
          <label htmlFor="confirmIssue" style={{ fontSize: '13px', color: 'var(--navy)', fontWeight: 600, cursor: 'pointer', flex: 1 }}>
            Azonnal kiállítom a számlát
            <div style={{ fontWeight: 400, fontSize: '11px', color: 'var(--slate)', marginTop: '2px' }}>
              {confirmIssue ? 'A számla véglegesítve és PDF-ként mentve lesz. Utána nem módosítható.' : 'A számla vázlatként mentődik, később kiállíthatod.'}
            </div>
          </label>
        </div>

      </div>
    </Modal>
  );
}

// ── Helyesbítő számla modal ──

interface OriginalLine {
  item_name_snapshot: string;
  quantity: number;
  unit: string;
  unit_price_net: number;
  discount_percent: number;
  vat_rate: string;
  line_net: number;
  line_vat: number;
  line_gross: number;
}

interface CorrectiveLineDraft {
  itemName: string;
  quantity: number;
  unit: string;
  unitPriceNet: number;
  discountPercent: number;
  vatRate: string;
}

function CorrectiveInvoiceModal({ sourceInvoice, onClose, onDone }: { sourceInvoice: Invoice; onClose: () => void; onDone: () => void }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [originalLines, setOriginalLines] = useState<OriginalLine[]>([]);
  const [lines, setLines] = useState<CorrectiveLineDraft[]>([]);
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0, 10));
  const [performanceDate, setPerformanceDate] = useState(sourceInvoice.performance_date || new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  // Eredeti számla sorok betöltése
  useEffect(() => {
    (async () => {
      try {
        const inv = await get<any>(`/invoices/${sourceInvoice.id}`);
        const srcLines: OriginalLine[] = (inv.lines || []).map((l: any) => ({
          item_name_snapshot: l.item_name_snapshot || 'Tétel',
          quantity: Number(l.quantity),
          unit: l.unit || 'db',
          unit_price_net: Number(l.unit_price_net),
          discount_percent: Number(l.discount_percent || 0),
          vat_rate: l.vat_rate || '27',
          line_net: Number(l.line_net),
          line_vat: Number(l.line_vat),
          line_gross: Number(l.line_gross),
        }));
        setOriginalLines(srcLines);
        // Előkitöltjük a helyesbítő sorokat az eredeti értékekkel
        setLines(srcLines.map(l => ({
          itemName: l.item_name_snapshot,
          quantity: l.quantity,
          unit: l.unit,
          unitPriceNet: l.unit_price_net,
          discountPercent: l.discount_percent,
          vatRate: l.vat_rate,
        })));
        if (inv.performance_date) setPerformanceDate(inv.performance_date);
      } catch (e: any) {
        toast('Nem sikerült betölteni a számla adatait.', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [sourceInvoice.id]);

  function calcLineNet(l: CorrectiveLineDraft) {
    const net = l.quantity * l.unitPriceNet;
    return l.discountPercent > 0 ? Math.round(net * (1 - l.discountPercent / 100)) : net;
  }
  function calcLineVat(l: CorrectiveLineDraft) {
    return Math.round(calcLineNet(l) * Number(l.vatRate) / 100);
  }

  // Új tételek összesítője
  const newTotals = lines.reduce((s, l) => {
    const net = calcLineNet(l);
    const vat = calcLineVat(l);
    return { net: s.net + net, vat: s.vat + vat, gross: s.gross + net + vat };
  }, { net: 0, vat: 0, gross: 0 });

  // Eredeti összesítő
  const origTotals = originalLines.reduce((s, l) => ({
    net: s.net + l.line_net, vat: s.vat + l.line_vat, gross: s.gross + l.line_gross,
  }), { net: 0, vat: 0, gross: 0 });

  // Különbség
  const diff = {
    net: newTotals.net - origTotals.net,
    vat: newTotals.vat - origTotals.vat,
    gross: newTotals.gross - origTotals.gross,
  };

  function addLine() {
    setLines(prev => [...prev, { itemName: '', quantity: 1, unit: 'db', unitPriceNet: 0, discountPercent: 0, vatRate: '27' }]);
  }

  async function handleSubmit() {
    if (!lines.some(l => l.itemName && l.quantity !== 0)) {
      toast('Adj meg legalább egy tételt névvel és nem nulla mennyiséggel.', 'error');
      return;
    }
    if (!reason.trim()) {
      toast('Add meg a helyesbítés indoklását.', 'error');
      return;
    }

    // Ellenőrizzük, hogy tényleg van-e változás
    const hasChange = lines.length !== originalLines.length || lines.some((l, i) => {
      const o = originalLines[i];
      if (!o) return true;
      return l.itemName !== o.item_name_snapshot || l.quantity !== o.quantity ||
        l.unitPriceNet !== o.unit_price_net || l.vatRate !== o.vat_rate ||
        l.discountPercent !== o.discount_percent || l.unit !== o.unit;
    });
    if (!hasChange) {
      toast('A helyesbítő tételek megegyeznek az eredetiekkel. Módosíts legalább egy tételt.', 'error');
      return;
    }

    setSaving(true);
    try {
      await post(`/invoices/${sourceInvoice.id}/correct`, {
        issueDate,
        performanceDate,
        reason: reason.trim(),
        lines: lines.filter(l => l.itemName).map(l => ({
          itemName: l.itemName,
          quantity: l.quantity,
          unit: l.unit,
          unitPriceNet: l.unitPriceNet,
          discountPercent: l.discountPercent,
          vatRate: l.vatRate,
        })),
      });
      toast('Helyesbítő számla kiállítva.', 'success');
      onDone();
    } catch (e: any) {
      const msg = e.message || 'Hiba a helyesbítő számla létrehozásakor.';
      toast(msg, 'error');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <Modal isOpen={true} onClose={onClose} title="Helyesbítő számla">
        <div style={{ padding: '20px', textAlign: 'center', color: 'var(--slate)' }}>Betöltés...</div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={true} onClose={onClose} title="Helyesbítő számla" size="lg">
      <div className="space-y-4" style={{ maxHeight: '75vh', overflowY: 'auto' }}>

        {/* Eredeti számla info */}
        <div style={{ background: 'var(--mist)', borderRadius: 'var(--r)', padding: '12px' }}>
          <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--navy)', marginBottom: '4px' }}>Eredeti számla</div>
          <div style={{ fontSize: '12px', color: 'var(--slate)' }}>
            <span style={{ fontWeight: 600 }}>{sourceInvoice.invoice_number}</span>
            {' \u2014 '}
            {sourceInvoice.partner_snapshot_json?.name || sourceInvoice.partner_name || ''}
            {' \u2014 '}
            {formatPrice(sourceInvoice.subtotal_gross)}
          </div>
        </div>

        {/* Eredeti tételek — csak olvasható */}
        <div>
          <label className="form-label" style={{ color: 'var(--slate)' }}>Eredeti tételek (nem módosítható)</label>
          <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r)', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            {originalLines.map((l, i) => (
              <div key={i} style={{ padding: '8px 12px', borderBottom: i < originalLines.length - 1 ? '1px solid var(--line-2)' : undefined, fontSize: '12px', color: 'var(--slate)', background: '#f9fafb' }}>
                <div style={{ fontWeight: 500, marginBottom: '2px' }}>{l.item_name_snapshot}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', fontSize: '11px' }}>
                  <span>{l.quantity} {l.unit}</span>
                  <span>× {formatPrice(l.unit_price_net)}</span>
                  <span>{l.vat_rate}%</span>
                  <span style={{ fontWeight: 600, marginLeft: 'auto' }}>{formatPrice(l.line_gross)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Helyesbített tételek — szerkeszthető */}
        <div>
          <label className="form-label">Helyesbített tételek</label>
          <div style={{ fontSize: '11px', color: 'var(--slate)', marginBottom: '6px' }}>Módosítsd a tételeket a helyes értékekre. A rendszer automatikusan kiszámolja a különbséget.</div>
          {lines.map((l, i) => (
            <div key={i} style={{ border: '1px solid var(--line)', borderRadius: 'var(--r)', padding: '10px 12px', marginBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                <input value={l.itemName} onChange={e => { const n = [...lines]; n[i] = { ...n[i], itemName: e.target.value }; setLines(n); }} placeholder="Tétel neve *" className="form-input" style={{ flex: 1 }} />
                <button onClick={() => setLines(lines.filter((_, j) => j !== i))} style={{ color: 'var(--red)', border: 'none', background: 'none', cursor: 'pointer', fontSize: '18px', padding: '2px 6px', flexShrink: 0 }}>×</button>
              </div>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div><label style={{ fontSize: '10px', color: 'var(--slate)' }}>Menny.</label>
                  <NumInput value={l.quantity} onChange={v => { const n = [...lines]; n[i] = { ...n[i], quantity: v }; setLines(n); }} className="form-input" style={{ width: '55px', textAlign: 'center' }} /></div>
                <div><label style={{ fontSize: '10px', color: 'var(--slate)' }}>Egys.</label>
                  <input value={l.unit} onChange={e => { const n = [...lines]; n[i] = { ...n[i], unit: e.target.value }; setLines(n); }} className="form-input" style={{ width: '45px', textAlign: 'center' }} /></div>
                <div style={{ flex: 1, minWidth: '80px' }}><label style={{ fontSize: '10px', color: 'var(--slate)' }}>Nettó ár</label>
                  <MoneyInput value={l.unitPriceNet} onChange={v => { const n = [...lines]; n[i] = { ...n[i], unitPriceNet: v }; setLines(n); }} placeholder="0" /></div>
                <div><label style={{ fontSize: '10px', color: 'var(--slate)' }}>Kedv.%</label>
                  <NumInput value={l.discountPercent} onChange={v => { const n = [...lines]; n[i] = { ...n[i], discountPercent: v }; setLines(n); }} min={0} max={100} className="form-input" style={{ width: '50px', textAlign: 'center' }} /></div>
                <div><label style={{ fontSize: '10px', color: 'var(--slate)' }}>ÁFA</label>
                  <select value={l.vatRate} onChange={e => { const n = [...lines]; n[i] = { ...n[i], vatRate: e.target.value }; setLines(n); }} className="form-input" style={{ width: '58px' }}>
                    <option value="27">27%</option><option value="18">18%</option><option value="5">5%</option><option value="0">0%</option>
                  </select></div>
              </div>
            </div>
          ))}
          <button onClick={addLine} style={{ fontSize: '13px', color: 'var(--orange)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>+ Új tétel</button>
        </div>

        {/* Dátumok */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <Input label="Kibocsátás dátuma *" type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)} />
          <Input label="Teljesítés dátuma" type="date" value={performanceDate} onChange={e => setPerformanceDate(e.target.value)} />
        </div>

        {/* Indoklás */}
        <div>
          <label className="form-label">Helyesbítés indoklása *</label>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} className="form-input" placeholder="Pl. Mennyiségi hiba javítása, árkorrekció..." maxLength={2000} />
        </div>

        {/* Összesítő — eredeti vs. helyesbített */}
        <div style={{ background: 'var(--mist)', borderRadius: 'var(--r)', padding: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--slate)' }}>
            <span>Eredeti nettó</span><span>{formatPrice(origTotals.net)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--slate)' }}>
            <span>Helyesbített nettó</span><span>{formatPrice(newTotals.net)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--slate)', borderTop: '1px solid var(--line)', paddingTop: '4px', marginTop: '4px' }}>
            <span>Eredeti bruttó</span><span>{formatPrice(origTotals.gross)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--slate)' }}>
            <span>Helyesbített bruttó</span><span>{formatPrice(newTotals.gross)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', fontWeight: 700, color: diff.gross >= 0 ? 'var(--navy)' : 'var(--red)', borderTop: '1px solid var(--line)', paddingTop: '6px', marginTop: '4px' }}>
            <span>Különbség (bruttó)</span><span>{diff.gross >= 0 ? '+' : ''}{formatPrice(diff.gross)}</span>
          </div>
        </div>

        {/* Figyelmeztetés */}
        <div style={{ background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 'var(--r)', padding: '10px 12px', fontSize: '12px', color: '#92400e' }}>
          A helyesbítő számla azonnal kiállításra kerül (nem vázlat). Az eredeti számla helyesbítettként lesz megjelölve. A NAV-hoz automatikusan beküldésre kerül.
        </div>

      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '9px', paddingTop: '12px', borderTop: '1px solid var(--line)', marginTop: '12px' }}>
        <Button variant="ghost" onClick={onClose}>Mégse</Button>
        <Button onClick={handleSubmit} loading={saving}>Helyesbítő számla kiállítása</Button>
      </div>
    </Modal>
  );
}
